import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const SITE_NAME = "Absolin - Infinite Digital Possibilities";
const SITE_URL = "https://absolinsoft.com";
const DEFAULT_IMAGE = "https://absolinsoft.com/og-image.png";
const DEFAULT_DESCRIPTION = "Absolin helps MSMEs & SMEs transform with ERP solutions, AI consulting, and digital transformation. Explore Odoo ERP, Focus X ERP, custom web app development, and industry-specific solutions.";

// Module-level cache for SEO integrations (GA4/GSC/GTM/Clarity) — fetched once per page load
let _cachedIntegrations = null;
let _integrationsPromise = null;
async function fetchIntegrations() {
  if (_cachedIntegrations) return _cachedIntegrations;
  if (_integrationsPromise) return _integrationsPromise;
  _integrationsPromise = api.get("/seo/integrations").then(r => {
    _cachedIntegrations = r.data || {};
    return _cachedIntegrations;
  }).catch(() => { _cachedIntegrations = {}; return {}; });
  return _integrationsPromise;
}

// Allow admin save flow to bust the cache without a full page reload
if (typeof window !== "undefined") {
  window.addEventListener("seo-integrations-updated", () => {
    _cachedIntegrations = null;
    _integrationsPromise = null;
  });
}

// Sanitise IDs before injecting into inline <script> bodies — defense-in-depth
const _safeId = (val, pattern) => {
  if (!val) return "";
  return pattern.test(String(val)) ? String(val) : "";
};
const _GA4_RE = /^G-[A-Z0-9]{4,16}$/;
const _GTM_RE = /^GTM-[A-Z0-9]{4,12}$/;
const _CLARITY_RE = /^[a-z0-9]{4,32}$/;
const _GSC_RE = /^[A-Za-z0-9_-]{20,200}$/;

export function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = "",
  canonicalUrl = "",
  ogType = "website",
  ogImage = DEFAULT_IMAGE,
  noindex = false,
  jsonLd = null,
  breadcrumbs = null,
  faqItems = null,
  howToSteps = null,
  howToName = "",
  howToDescription = "",
  howToTotalTime = "",
  integrations: integrationsProp = null, // override; otherwise auto-fetched from /api/seo/integrations
}) {
  const [autoIntegrations, setAutoIntegrations] = useState(_cachedIntegrations);
  useEffect(() => {
    if (integrationsProp) return;
    if (_cachedIntegrations) { setAutoIntegrations(_cachedIntegrations); return; }
    let mounted = true;
    fetchIntegrations().then(d => { if (mounted) setAutoIntegrations(d); });
    return () => { mounted = false; };
  }, [integrationsProp]);
  const integrations = integrationsProp || autoIntegrations;

  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const baseKeywords = "ERP solutions India, Odoo ERP, Focus X ERP, digital transformation MSME, AI consulting SME, business digitalisation, web app development, custom software development";
  const allKeywords = keywords ? `${keywords}, ${baseKeywords}` : baseKeywords;

  const breadcrumbLd = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": b.name,
      "item": b.url ? `${SITE_URL}${b.url}` : undefined,
    }))
  } : null;

  const faqLd = faqItems && faqItems.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    }))
  } : null;

  const howToLd = howToSteps && howToSteps.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": howToName || (title || "Implementation Plan"),
    "description": howToDescription || description,
    ...(howToTotalTime ? { "totalTime": howToTotalTime } : {}),
    "step": howToSteps.map((s, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "name": s.name || s.title || `Step ${i + 1}`,
      "text": s.text || s.description || "",
    }))
  } : null;

  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Absolin Software Solutions",
    "url": SITE_URL,
    "logo": `${SITE_URL}/absolin-logo.png`,
    "description": "Leading ERP and digital transformation company for MSMEs & SMEs in India",
    "sameAs": [
      "https://www.linkedin.com/company/absolin",
      "https://www.youtube.com/@absolin"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-829-755-5678",
      "contactType": "sales",
      "areaServed": "IN",
      "availableLanguage": ["English", "Hindi", "Tamil"]
    }
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {canonicalUrl && <link rel="canonical" href={`${SITE_URL}${canonicalUrl}`} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      {canonicalUrl && <meta property="og:url" content={`${SITE_URL}${canonicalUrl}`} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD: Organization */}
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>

      {/* JSON-LD: Custom */}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}

      {/* JSON-LD: Breadcrumbs */}
      {breadcrumbLd && <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>}

      {/* JSON-LD: FAQ */}
      {faqLd && <script type="application/ld+json">{JSON.stringify(faqLd)}</script>}

      {/* JSON-LD: HowTo */}
      {howToLd && <script type="application/ld+json">{JSON.stringify(howToLd)}</script>}

      {/* Google Search Console verification */}
      {_safeId(integrations?.gsc_verification, _GSC_RE) && (
        <meta name="google-site-verification" content={_safeId(integrations.gsc_verification, _GSC_RE)} />
      )}

      {/* Google Analytics 4 */}
      {_safeId(integrations?.ga4_id, _GA4_RE) && (
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${_safeId(integrations.ga4_id, _GA4_RE)}`} />
      )}
      {_safeId(integrations?.ga4_id, _GA4_RE) && (
        <script>
          {`window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${_safeId(integrations.ga4_id, _GA4_RE)}');`}
        </script>
      )}

      {/* Google Tag Manager */}
      {_safeId(integrations?.gtm_id, _GTM_RE) && (
        <script>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${_safeId(integrations.gtm_id, _GTM_RE)}');`}
        </script>
      )}

      {/* Microsoft Clarity */}
      {_safeId(integrations?.ms_clarity_id, _CLARITY_RE) && (
        <script>
          {`(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${_safeId(integrations.ms_clarity_id, _CLARITY_RE)}");`}
        </script>
      )}
    </Helmet>
  );
}
